// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- fallback for insecure contexts where crypto.randomUUID is unavailable
import { v4 as uuidv4 } from 'uuid';

import { isRecord } from '@atlassian/conversation-assistant-utils/isRecord';
import type { ExperienceEvent, FailEvent } from '@atlassian/experience-tracker/ExperienceEvent';
import type {
	CollectFn,
	ExperienceTrackerAPI,
} from '@atlassian/experience-tracker/ExperienceTracker';

import { ExperienceName } from './constants';
import { remixCreateTerminalOwnership } from './remix-create-terminal-ownership';
import { getRemixFeatureArea } from './remix-feature-area';
import { REMIX_IMAGE_GENERATION_SAFETY_REFUSAL_OUTCOME } from './remix-outcomes';

const randomId = (): string => {
	if (globalThis?.crypto?.randomUUID) {
		return globalThis.crypto.randomUUID();
	}

	// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- fallback for insecure contexts where crypto.randomUUID is unavailable
	return uuidv4();
};

const SEND_MESSAGE_EXPERIENCE_NAMES = new Set<string>([ExperienceName.SEND_MESSAGE]);

const CREATE_TERMINAL_EXPERIENCE_NAMES = new Set<string>([
	ExperienceName.REMIX_RENDERS_IN_MODAL,
	ExperienceName.REMIX_INSERT,
]);

const ROVO_SIDEBAR_HANDOFF_REASON = 'rovo_sidebar_handoff';

const IMAGE_GENERATION_SAFETY_ERROR_CODES = new Set([
	'ACCEPTABLE_USE_VIOLATIONS',
	'IMAGE_SAFETY_REJECTED',
	'SAFETY_VIOLATION',
]);

export type RemixCreateAttributes = {
	entryPoint?: string;
	featureArea?: string;
	remixSource?: string;
	product?: string;
	remixType?: string;
	remixSubtype?: string;
	[key: string]: unknown;
};

type RemixCreateCollectContext = {
	/** The remix conversation id, used to correlate the send-message failure. */
	conversationChannelId?: string;
	/** A unique id generated when this REMIX_CREATE parent starts passed to all sub experiences */
	remixCreateId?: string;
	/** Attributes captured when the open-menu sub-experience starts. */
	openMenuAttributes?: RemixCreateAttributes;
	/** The latest choice made before the Remix is submitted. */
	pendingSelectionAttributes?: RemixCreateAttributes;
	/** Prevents duplicate or late settlement when the overlay rerenders or the flow is aborted. */
	isOpenMenuSettled?: boolean;
	/** Prevents duplicate settlement when explicit cancellation is followed by component cleanup. */
	isCreateSettled?: boolean;
	/** Feature area retained across the compound experience's terminal outcomes. */
	featureArea?: string;
	allowCollectFallback?: boolean;
};

const toError = (error: Error | undefined): Error => error ?? new Error('aiMateSendMessage failed');

const getErrorCode = (error: unknown): string | undefined => {
	if (!isRecord(error)) {
		return undefined;
	}

	const errorAttributes = isRecord(error.errorAttributes) ? error.errorAttributes : undefined;
	if (typeof errorAttributes?.errorCode === 'string') {
		return errorAttributes.errorCode;
	}

	const detail = isRecord(error.detail) ? error.detail : undefined;
	const message = isRecord(detail?.message) ? detail.message : undefined;
	return typeof message?.message_template === 'string' ? message.message_template : undefined;
};

const getExpectedGenerationSuccessOutcome = (
	event: ExperienceEvent,
): typeof REMIX_IMAGE_GENERATION_SAFETY_REFUSAL_OUTCOME | undefined => {
	const attributes = event.attributes as Record<string, unknown> | undefined;
	if (attributes?.isSafetyViolation === true) {
		return REMIX_IMAGE_GENERATION_SAFETY_REFUSAL_OUTCOME;
	}

	const error = 'error' in event ? event.error : undefined;
	const errorCode = getErrorCode(error) ?? attributes?.errorType;
	if (typeof errorCode === 'string' && IMAGE_GENERATION_SAFETY_ERROR_CODES.has(errorCode)) {
		return REMIX_IMAGE_GENERATION_SAFETY_REFUSAL_OUTCOME;
	}

	return undefined;
};

const withFeatureArea = (
	attributes: Record<string, unknown> | undefined,
	context: RemixCreateCollectContext,
): Record<string, unknown> => {
	const featureArea = context.featureArea ?? attributes?.featureArea;
	return { ...attributes, ...(featureArea !== undefined && { featureArea }) };
};

let activeCreateCollectContext: RemixCreateCollectContext | undefined;

const settleCreateContext = (context: RemixCreateCollectContext): void => {
	context.isCreateSettled = true;
	remixCreateTerminalOwnership.release(context.remixCreateId);
};

const matchesTerminal = (event: ExperienceEvent, context: RemixCreateCollectContext): boolean => {
	if (!CREATE_TERMINAL_EXPERIENCE_NAMES.has(event.name)) {
		return false;
	}

	const attributes = event.attributes as Record<string, unknown> | undefined;

	const eventRemixCreateId = attributes?.remixCreateId;
	if (context.remixCreateId && typeof eventRemixCreateId === 'string') {
		return eventRemixCreateId === context.remixCreateId;
	}

	const eventConversationId = attributes?.conversationId;
	if (context.conversationChannelId && typeof eventConversationId === 'string') {
		return eventConversationId === context.conversationChannelId;
	}

	return Boolean(context.allowCollectFallback);
};

const matchesSendMessageFailure = (
	event: ExperienceEvent,
	context: RemixCreateCollectContext,
): boolean => {
	if (!SEND_MESSAGE_EXPERIENCE_NAMES.has(event.name) || event.action !== 'taskFail') {
		return false;
	}

	const attributes = event.attributes as Record<string, unknown> | undefined;
	if (attributes?.isRemixCreate !== true) {
		return false;
	}

	const requestConversationId = attributes?.conversationId;
	if (context.conversationChannelId && requestConversationId === context.conversationChannelId) {
		return true;
	}

	// If the event's conversationId is unavailable, fall back to the single-inflight
	// assumption (there is only one remix create stream at a time).
	return Boolean(context.allowCollectFallback);
};

/**
 * Creates a `collect` function for the REMIX_CREATE compound experience.
 *
 * Responsibilities:
 * - SUCCEED the parent with a queryable outcome when image generation returns a safety refusal.
 * - FAIL the parent when the correlated aiMateSendMessage request otherwise FAILS
 *   (correlated by conversationId, mirroring createRemixRegenerateCollect).
 * - FAIL / ABORT the parent when a terminal render/insert experience fails/aborts.
 * - SUCCEED the parent after a terminal experience succeeds AND no further terminal
 *   starts within a short quiet window (captures multi-step edit flows).
 *
 * The send-message flow itself is never modified — this only OBSERVES events.
 */
export const createRemixCreateCollect = (context: RemixCreateCollectContext): CollectFn => {
	let hasStopped = false;

	return (events, experience): void => {
		if (hasStopped || context.isCreateSettled) {
			hasStopped = true;
			return;
		}

		for (const event of events) {
			// 1) Upstream generation-stream failure → succeed for a handled safety refusal,
			// otherwise fail the parent.
			if (matchesSendMessageFailure(event, context)) {
				hasStopped = true;
				settleCreateContext(context);
				const attributes = event.attributes as Record<string, unknown> | undefined;
				const successOutcome = getExpectedGenerationSuccessOutcome(event);
				if (successOutcome) {
					experience.succeed({
						attributes: {
							...withFeatureArea(attributes, context),
							outcome: successOutcome,
							succeededVia: event.name,
						},
					});
					return;
				}
				experience.fail({
					error: toError((event as FailEvent).error),
					attributes: {
						...withFeatureArea(attributes, context),
						failureSource: 'send_message_collect',
						failureStage: 'request',
						upstreamExperience: event.name,
					},
				});
				return;
			}

			if (!matchesTerminal(event, context)) {
				continue;
			}

			const attributes = event.attributes as Record<string, unknown> | undefined;

			// 2) A terminal experience FAILED → succeed for a handled safety refusal,
			// otherwise fail the parent.
			if (event.action === 'taskFail') {
				hasStopped = true;
				settleCreateContext(context);
				const successOutcome = getExpectedGenerationSuccessOutcome(event);
				if (successOutcome) {
					experience.succeed({
						attributes: {
							...withFeatureArea(attributes, context),
							outcome: successOutcome,
							succeededVia: event.name,
						},
					});
					return;
				}
				experience.fail({
					error: toError((event as FailEvent).error),
					attributes: {
						...withFeatureArea(attributes, context),
						failureSource: 'terminal_collect',
						failureStage: 'render',
						upstreamExperience: event.name,
					},
				});
				return;
			}

			// 3) A terminal experience ABORTED → abort the parent.
			if (event.action === 'taskAbort') {
				hasStopped = true;
				settleCreateContext(context);
				experience.abort({
					reason: `terminal_${event.name}_aborted`,
					attributes: {
						...withFeatureArea(attributes, context),
						upstreamExperience: event.name,
					},
				});
				return;
			}

			// 4) A safety-marked terminal is a handled response, so succeed the reliability
			// parent with an explicit outcome.
			if (event.action === 'taskSuccess') {
				hasStopped = true;
				settleCreateContext(context);
				const successOutcome = getExpectedGenerationSuccessOutcome(event);
				if (successOutcome) {
					experience.succeed({
						attributes: {
							...withFeatureArea(attributes, context),
							outcome: successOutcome,
							succeededVia: event.name,
						},
					});
					return;
				}

				// An ordinary terminal success succeeds the parent immediately.
				experience.succeed({
					attributes: {
						...withFeatureArea(attributes, context),
						succeededVia: event.name,
					},
				});
				return;
			}
		}
	};
};

/**
 * Starts the REMIX_CREATE compound (parent) experience at a remix entry point and,
 * for chooser-owned flows, its `open-menu` sub-experience. Direct-prompt flows can omit
 * `open-menu`. The parent's `collect` is wired to
 * {@link createRemixCreateCollect} so a correlated aiMateSendMessage failure fails
 * the parent, and a terminal render/insert experience settles it.
 *
 * The conversation does not exist yet at the entry point, so `collect` uses the
 * single-inflight fallback; call {@link updateRemixCreateConversationId} once the
 * conversation id is known to stamp it onto the running experience.
 */
export const startRemixCreateExperience = ({
	experienceTracker,
	attributes,
	includeOpenMenuExperience = true,
}: {
	experienceTracker: ExperienceTrackerAPI;
	attributes?: RemixCreateAttributes;
	includeOpenMenuExperience?: boolean;
}): void => {
	const remixCreateId = randomId();

	const featureArea = getRemixFeatureArea({
		remixType: attributes?.remixType,
		existingFeatureArea: attributes?.featureArea,
	});
	const startAttributes = {
		...attributes,
		...(featureArea !== undefined && { featureArea }),
		remixCreateId,
	};

	const collectContext: RemixCreateCollectContext = {
		remixCreateId,
		...(includeOpenMenuExperience && { openMenuAttributes: startAttributes }),
		featureArea,
		allowCollectFallback: true,
	};
	activeCreateCollectContext = collectContext;

	experienceTracker.start({
		name: ExperienceName.REMIX_CREATE,
		id: remixCreateId,
		attributes: startAttributes,
		collect: createRemixCreateCollect(collectContext),
	});
	if (includeOpenMenuExperience) {
		experienceTracker.start({
			name: ExperienceName.REMIX_OPEN_MENU,
			id: ExperienceName.REMIX_OPEN_MENU,
			attributes: startAttributes,
		});
	}
};

export const getActiveRemixCreateId = (): string | undefined =>
	activeCreateCollectContext?.isCreateSettled
		? undefined
		: activeCreateCollectContext?.remixCreateId;

/**
 * Succeeds the open-menu sub-experience once the Remix chooser has actually rendered.
 * A start without this success therefore identifies a flow where the expected UI did not open.
 */
export const succeedRemixOpenMenuExperience = (experienceTracker: ExperienceTrackerAPI): void => {
	const context = activeCreateCollectContext;
	if (!context?.openMenuAttributes || context.isOpenMenuSettled) {
		return;
	}

	context.isOpenMenuSettled = true;
	experienceTracker.succeed({
		name: ExperienceName.REMIX_OPEN_MENU,
		attributes: context.openMenuAttributes,
	});
};

/** Fails the open-menu milestone if a technical error prevents the chooser from rendering. */
export const failRemixOpenMenuExperience = (
	experienceTracker: ExperienceTrackerAPI,
	error: Error,
): void => {
	const context = activeCreateCollectContext;
	if (!context?.openMenuAttributes || context.isOpenMenuSettled) {
		return;
	}

	context.isOpenMenuSettled = true;
	experienceTracker.fail({
		name: ExperienceName.REMIX_OPEN_MENU,
		error,
		attributes: context.openMenuAttributes,
	});
};

/**
 * Succeeds REMIX_CREATE when generation is handed off to AIFC.
 * REMIX_AIFC_HANDOFF remains an independent capability experience rather than
 * being collected as a REMIX_CREATE sub-experience.
 */
export const succeedRemixCreateExperienceAtHandoff = (
	experienceTracker: ExperienceTrackerAPI,
	attributes?: RemixCreateAttributes,
): void => {
	const context = activeCreateCollectContext;
	if (!context || context.isCreateSettled) {
		return;
	}

	settleCreateContext(context);
	experienceTracker.succeed({
		name: ExperienceName.REMIX_CREATE,
		attributes: {
			...context.openMenuAttributes,
			...attributes,
			succeededVia: ExperienceName.REMIX_AIFC_HANDOFF,
		},
	});
};

/** Settles Object Sidebar infographic create after its Rovo sidebar handoff is acknowledged. */
export const succeedRemixCreateExperienceAtSidebarHandoff = (
	experienceTracker: ExperienceTrackerAPI,
	isObjectSidebarPopup: boolean,
): void => {
	const context = activeCreateCollectContext;
	if (
		!context ||
		context.isCreateSettled ||
		!isObjectSidebarPopup ||
		context.openMenuAttributes?.entryPoint !== 'objectSidebar' ||
		context.pendingSelectionAttributes?.remixType !== 'infographic'
	) {
		return;
	}

	settleCreateContext(context);
	experienceTracker.succeed({
		name: ExperienceName.REMIX_CREATE,
		attributes: {
			...context.openMenuAttributes,
			...context.pendingSelectionAttributes,
			...(context.conversationChannelId && { conversationId: context.conversationChannelId }),
			succeededVia: ROVO_SIDEBAR_HANDOFF_REASON,
		},
	});
};

/** Succeeds REMIX_CREATE when image generation returns a handled safety-policy refusal. */
export const succeedRemixCreateExperienceForSafetyRefusal = (
	experienceTracker: ExperienceTrackerAPI,
): void => {
	const context = activeCreateCollectContext;
	if (!context || context.isCreateSettled) {
		return;
	}

	settleCreateContext(context);
	experienceTracker.succeed({
		name: ExperienceName.REMIX_CREATE,
		attributes: {
			...context.openMenuAttributes,
			...context.pendingSelectionAttributes,
			outcome: REMIX_IMAGE_GENERATION_SAFETY_REFUSAL_OUTCOME,
		},
	});
};

/** Fails REMIX_CREATE when a technical error prevents the flow from reaching handoff. */
export const failRemixCreateExperience = (
	experienceTracker: ExperienceTrackerAPI,
	error: Error,
	attributes?: RemixCreateAttributes,
): void => {
	const context = activeCreateCollectContext;
	if (!context || context.isCreateSettled) {
		return;
	}

	settleCreateContext(context);
	experienceTracker.fail({
		name: ExperienceName.REMIX_CREATE,
		error,
		attributes: {
			...context.openMenuAttributes,
			...withFeatureArea(attributes, context),
		},
	});
};

/**
 * Aborts the REMIX_CREATE compound experience (and its non-gating open-menu
 * milestone) when the flow is abandoned before reaching a terminal — e.g. the
 * user closes the remix overlay/modal or cancels remix thinking without
 * committing an option.
 */
export const abortRemixCreateExperience = (
	experienceTracker: ExperienceTrackerAPI,
	reason: string,
): void => {
	const context = activeCreateCollectContext;
	if (!context) {
		return;
	}

	const attributes = {
		...context.openMenuAttributes,
		...context.pendingSelectionAttributes,
		reason,
	};
	if (context.openMenuAttributes && !context.isOpenMenuSettled) {
		context.isOpenMenuSettled = true;
		experienceTracker.abort({
			name: ExperienceName.REMIX_OPEN_MENU,
			reason,
			attributes,
		});
	}

	if (
		!context.isCreateSettled &&
		!remixCreateTerminalOwnership.isOwnedByTerminal(context.remixCreateId)
	) {
		settleCreateContext(context);
		experienceTracker.abort({
			name: ExperienceName.REMIX_CREATE,
			reason,
			attributes,
		});
	}
};

export const abortRemixCreateExperienceIfNotCommitted = (
	experienceTracker: ExperienceTrackerAPI,
	hasCommitted: boolean,
	reason = 'User closed remix overlay',
): void => {
	if (!hasCommitted) {
		abortRemixCreateExperience(experienceTracker, reason);
	}
};

/**
 * Records a non-gating milestone sub-experience (select-option / show-loading) as
 * the flow advances: an instantaneous start-then-succeed. These are funnel
 * breadcrumbs; the parent's success is owned by the terminal experiences via
 * `collect`, not by these milestones.
 */
const recordRemixCreateMilestone = (
	experienceTracker: ExperienceTrackerAPI,
	name: string,
	attributes?: RemixCreateAttributes,
): void => {
	const remixCreateId = getActiveRemixCreateId();
	const milestoneAttributes = { ...attributes, remixCreateId };
	experienceTracker.start({ name, id: name, attributes: milestoneAttributes });
	experienceTracker.succeed({ name, attributes: milestoneAttributes });
};

// eslint-disable-next-line @atlaskit/platform/one-value-export-per-file -- This collector already exposes its cohesive lifecycle API from one entry point.
export const updateRemixCreatePendingSelection = ({
	remixType,
	remixSubtype,
}: {
	remixType: string | undefined;
	remixSubtype: string | undefined;
}): void => {
	if (!activeCreateCollectContext || activeCreateCollectContext.isCreateSettled) {
		return;
	}
	activeCreateCollectContext.pendingSelectionAttributes =
		remixType === undefined && remixSubtype === undefined
			? undefined
			: {
					remixType,
					remixSubtype,
				};
};

export const trackRemixCreateOptionSelected = ({
	experienceTracker,
	conversationId,
	remixType,
	remixSubtype,
	includesFreegenPrompt,
}: {
	experienceTracker: ExperienceTrackerAPI;
	conversationId: string | undefined;
	remixType: string | undefined;
	remixSubtype: string | undefined;
	includesFreegenPrompt?: boolean;
}): void => {
	if (conversationId) {
		updateRemixCreateConversationId(experienceTracker, conversationId);
	}
	const featureArea = getRemixFeatureArea({
		remixType,
		existingFeatureArea: activeCreateCollectContext?.featureArea,
	});
	if (activeCreateCollectContext) {
		activeCreateCollectContext.featureArea = featureArea;
	}
	const attributes = {
		remixType,
		remixSubtype,
		includesFreegenPrompt,
		...(featureArea !== undefined && { featureArea }),
	};
	if (activeCreateCollectContext) {
		activeCreateCollectContext.pendingSelectionAttributes = attributes;
	}
	experienceTracker.updateAttributes({ name: ExperienceName.REMIX_CREATE, attributes });
	recordRemixCreateMilestone(experienceTracker, ExperienceName.REMIX_SELECT_OPTION, attributes);
	recordRemixCreateMilestone(experienceTracker, ExperienceName.REMIX_SHOW_LOADING, attributes);
};

export const updateRemixCreateConversationId = (
	experienceTracker: ExperienceTrackerAPI,
	conversationId: string,
): void => {
	experienceTracker.updateAttributes({
		name: ExperienceName.REMIX_CREATE,
		attributes: { conversationId },
	});
	if (activeCreateCollectContext) {
		activeCreateCollectContext.conversationChannelId = conversationId;
	}
};
